// client/src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

// Prefer env-configured API URL, fall back to local dev
const API_URL = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  // ── 1. Stop FIRST (no dependencies) ──────────────────────────
  const stopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []); // ← no deps, safe to define first

  // ── 2. Silent refresh (depends on stopRefreshInterval) ───────
  const silentRefresh = useCallback(async () => {
    if (document.visibilityState === 'hidden') return;
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setUser(null);
        stopRefreshInterval();
        window.location.href = '/signin';
      }
    } catch (err) {
      console.warn('Silent refresh error:', err);
    }
  }, [stopRefreshInterval]); // ← stopRefreshInterval already defined ✅

  // ── 3. Start interval (depends on silentRefresh) ──────────────
  const startRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = setInterval(silentRefresh, 13 * 60 * 1000);
  }, [silentRefresh]); // ← silentRefresh already defined ✅

  // ── 4. checkAuth (depends on start + stop) ────────────────────
  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/me`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Not authenticated');
      const data = await res.json();
      setUser(data);
      startRefreshInterval();
      return data;
    } catch {
      setUser(null);
      stopRefreshInterval();
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, [startRefreshInterval, stopRefreshInterval]);

  // ── 5. Logout (depends on stop) ───────────────────────────────
  const logout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      stopRefreshInterval();
    }
  };

  // ── 6. Google OAuth ───────────────────────────────────────────
  const loginWithGoogle = (mode = 'signin') => {
    const popup = window.open(
      `${API_URL}/auth/google?state=${mode}`,
      'Google Login',
      'width=500,height=600,left=400,top=100'
    );

    const origin = window.location.origin;

    const handleMessage = async (event) => {
      if (event.origin !== origin) return;
      window.removeEventListener('message', handleMessage);

      if (event.data?.type === 'auth-success') {
        const userData = await checkAuth();
        if (userData) {
          const targetPath = event.data.onboarding ? '/?onboarding=true' : '/';
          window.location.replace(targetPath);
        } else {
          // Auth succeeded in popup but cookies not yet available - retry once after short delay
          await new Promise(r => setTimeout(r, 300));
          const retryData = await checkAuth();
          if (retryData) {
            const targetPath = event.data.onboarding ? '/?onboarding=true' : '/';
            window.location.replace(targetPath);
          }
          // If still failing, user stays on current page; refresh will pick up cookies
        }
      }

      if (event.data?.type === 'auth-error') {
        const { error, redirect } = event.data;
        window.location.replace(`/${redirect}?error=${error}`);
      }
    };

    window.addEventListener('message', handleMessage);

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        window.removeEventListener('message', handleMessage);
      }
    }, 500);
  };

  // ── App load ──────────────────────────────────────────────────
  useEffect(() => {
    checkAuth();
    return () => stopRefreshInterval();
  }, [checkAuth, stopRefreshInterval]);

  return (
    <AuthContext.Provider value={{ user, setUser, authLoading, checkAuth, logout, loginWithGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ── fetchWithAuth — safety net for any missed 401s ────────────
export const fetchWithAuth = async (url, options = {}) => {
  let res = await fetch(url, { ...options, credentials: 'include' });

  if (res.status === 401) {
    const refreshRes = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (refreshRes.ok) {
      res = await fetch(url, { ...options, credentials: 'include' });
    } else {
      window.location.href = '/signin';
    }
  }

  return res;
};
